import React, { useEffect } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { Spinner } from "@common/components/Spinner/Spinner";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { PlatformName } from "@common/models";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import useReleasesConsumer from "@main/pages/Release/consumers/useReleasesConsumer";
import { crashListLinkFor, feedbackListLinkFor } from "@main/pages/Release/helpers/ticketListLink";
import { AppHangFreeSessionsCard } from "@main/pages/Release/views/MetricCards/AppHangFreeSessionsCard";
import { CrashesVolumeCard } from "@main/pages/Release/views/MetricCards/CrashesVolumeCard";
import { CrashFreeSessionsCard } from "@main/pages/Release/views/MetricCards/CrashFreeSessionsCard";
import { CurrentDurationCard } from "@main/pages/Release/views/MetricCards/CurrentDurationCard";
import { EndpointHealthCard } from "@main/pages/Release/views/MetricCards/EndpointsHealthCard";
import { FeedbackVolumeCard } from "@main/pages/Release/views/MetricCards/FeedbackVolumeCard";
import { MeanTimeToResolveCard } from "@main/pages/Release/views/MetricCards/MeanTimeToResolveCard";
import { NetworkSpeedCard } from "@main/pages/Release/views/MetricCards/NetworkSpeedCard";
import { ReleaseDateEstimateCard } from "@main/pages/Release/views/MetricCards/ReleaseDateEstimateCard";
import { ReleaseHealthCard } from "@main/pages/Release/views/MetricCards/ReleaseHealthCard";
import { ReleaseSummaryCard } from "@main/pages/Release/views/MetricCards/ReleaseSummaryCard";
import { ResolutionRateCard } from "@main/pages/Release/views/MetricCards/ResolutionRateCard";
import { TestingCoverageCard } from "@main/pages/Release/views/MetricCards/TestingCoverageCard";
import { ReleaseVersionDropdown } from "@main/pages/Release/views/ReleaseVersionDropdown";
import { ReleasesModuleEmptyState } from "@main/pages/shared/components/ModuleEmptyState/ReleasesModuleEmptyState";
import { SdkSetupSteps } from "@main/pages/shared/components/SDKSetup/SdkSetupSteps";
import { MetricKeys } from "@main/router/config/routePaths";
import { useSearchParams } from "react-router-dom";

import * as Styled from "./ReleasesPageView.styles";

export default function ReleasesPageView() {
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "Release overview" });
  }, [sendEvent]);

  const { selectedApp } = useAppSelectionContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlVersion = searchParams.get("version");

  const {
    healthData,
    releaseVersions,
    selectedVersion,
    setSelectedVersion,
    releaseSearch,
    setReleaseSearch,
    endpointHealthData,
    networkSpeedData,
    error,
    releaseStatusLoading,
    releasesLoading,
    hasMore,
    isFetchingNextPage,
    loadNext,
    generateReleaseSummary,
    goToMetric,
    openTicketList,
  } = useReleasesConsumer(urlVersion);

  const handleVersionChange = (version: string) => {
    setSelectedVersion(version);
    setSearchParams({ version });
  };

  // The consumer resolves selectedVersion (preferring the URL param when it's a real version,
  // falling back to the latest release otherwise) — mirror that back into the URL so it stays
  // the single source of truth / a shareable link.
  useEffect(() => {
    if (!selectedVersion || searchParams.get("version") === selectedVersion) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("version", selectedVersion);
    setSearchParams(nextParams, { replace: true });
  }, [selectedVersion, searchParams, setSearchParams]);

  const CustomElement = (
    <ReleaseVersionDropdown
      releaseVersions={releaseVersions}
      selectedVersion={selectedVersion}
      searchValue={releaseSearch}
      onVersionChange={handleVersionChange}
      onSearchChange={setReleaseSearch}
      loading={releasesLoading}
      hasMore={hasMore}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={loadNext}
    />
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (!selectedApp?.is_approved) {
    return <SdkSetupSteps />;
  }

  if (error) {
    return (
      <ErrorStateView
        title="There was an error loading releases. Try refreshing."
        subtitle="The link you followed may be broken, or the workspace may have been removed."
      />
    );
  }

  if (!releasesLoading && !releaseSearch && releaseVersions.length === 0) {
    return <ReleasesModuleEmptyState />;
  }

  if (releaseStatusLoading || (releasesLoading && !selectedVersion)) {
    return (
      <>
        <PageWrap style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Spinner />
        </PageWrap>
      </>
    );
  }

  return (
    <>
      <PageWrap style={{ display: "flex", justifyContent: "center" }}>
        <Styled.ReleasesPageWrap>
          <Flex
            style={{ height: "41.4rem" }}
            $gap={1.2}
          >
            <Flex
              style={{ flexBasis: "26.8rem", minWidth: 0 }}
              $column
            >
              <ReleaseHealthCard
                score={healthData?.release_health_score}
                highlight
                onMoreClick={() => goToMetric(MetricKeys.releaseHealth)}
              />
            </Flex>
            <Flex
              $column
              $flex={1}
              $gap={1.2}
            >
              <ReleaseSummaryCard
                summary={healthData?.release_summary}
                onGenerateSummaryClick={generateReleaseSummary}
              />
            </Flex>
            <Flex
              style={{ flexBasis: "38.2rem", minWidth: 0 }}
              $column
              $gap={1.2}
            >
              <CrashFreeSessionsCard
                style={{ flex: 1 }}
                rate={healthData?.crash_free_sessions?.crash_free_rate ?? 0}
                score={healthData?.crash_free_sessions?.score}
                onMoreClick={() => goToMetric(MetricKeys.crashFreeSessions)}
              />

              {selectedApp?.platform.name === PlatformName.ANDROID ||
              selectedApp?.platform.name === PlatformName.IOS ? (
                <AppHangFreeSessionsCard
                  style={{ flex: 1 }}
                  rate={healthData?.anr_free_sessions?.anr_free_rate ?? 0}
                  totalAppHangs={healthData?.anr_free_sessions?.anr_sessions ?? 0}
                  score={healthData?.anr_free_sessions?.score}
                  onOpenAppHangsClick={(filters) => openTicketList(crashListLinkFor, filters)}
                  onMoreClick={() => goToMetric(MetricKeys.appHangFreeSessions)}
                  platform={selectedApp?.platform.name}
                />
              ) : null}

              <ResolutionRateCard
                rate={healthData?.resolution_rate?.rate ?? 0}
                newPoints={healthData?.resolution_rate?.new_points ?? 0}
                closedPoints={healthData?.resolution_rate?.closed_points ?? 0}
                score={healthData?.resolution_rate.score}
                onMoreClick={() => goToMetric(MetricKeys.resolutionRate)}
              />
            </Flex>
          </Flex>

          <Flex $gap={1.2}>
            <FeedbackVolumeCard
              totalTickets={healthData?.feedback_volume?.received_feedback_tickets ?? 0}
              closedTickets={healthData?.feedback_volume?.closed_feedback_tickets ?? 0}
              highPriorityTickets={healthData?.feedback_volume?.high_priority_tickets ?? 0}
              highPriorityClosedTickets={healthData?.feedback_volume?.closed_high_priority_tickets ?? 0}
              onOpenTickets={(filters) => openTicketList(feedbackListLinkFor, filters)}
            />
            {selectedApp?.platform.name === PlatformName.ANDROID || selectedApp?.platform.name === PlatformName.IOS ? (
              <CrashesVolumeCard
                totalCrashes={healthData?.crash_volume?.received_crash_groups ?? 0}
                closedCrashes={healthData?.crash_volume?.closed_crash_groups ?? 0}
                totalCrashEvents={healthData?.crash_volume?.received_crash_events ?? 0}
                onOpenCrashGroups={(filters) => openTicketList(crashListLinkFor, filters)}
              />
            ) : null}
          </Flex>

          <Flex $gap={1.2}>
            <Flex
              style={{ minWidth: 0, width: "60.8rem" }}
              $column
            >
              <EndpointHealthCard
                data={endpointHealthData}
                score={healthData?.network_health?.score}
                onMoreClick={() => goToMetric(MetricKeys.endpointHealth)}
              />
            </Flex>
            <Flex
              style={{ minWidth: 0, width: "52.4rem" }}
              $column
            >
              <NetworkSpeedCard
                data={networkSpeedData}
                score={healthData?.network_speed?.score}
                onMoreClick={() => goToMetric(MetricKeys.networkSpeed)}
              />
            </Flex>
          </Flex>

          <Flex $gap={1.2}>
            <TestingCoverageCard
              style={{ flex: 1 }}
              data={healthData?.testing_coverage}
              score={healthData?.testing_coverage?.score}
              onMoreClick={() => goToMetric(MetricKeys.testingCoverage)}
            />
          </Flex>

          <Flex
            $gap={1.2}
            style={{ height: "19.2rem" }}
          >
            <CurrentDurationCard
              style={{ flex: "0 0 26.8rem", minWidth: 0 }}
              days={healthData?.current_duration?.duration_days ?? 0}
              startDate={healthData?.current_duration?.first_ticket_at ?? ""}
              endDate={healthData?.current_duration?.last_ticket_at ?? ""}
            />

            <ReleaseDateEstimateCard
              style={{ height: "13rem", flex: "0 0 47.6rem", minWidth: 0, alignSelf: "flex-start" }}
              days={healthData?.release_date_estimate?.estimated_days_until_release}
            />

            <MeanTimeToResolveCard
              style={{ height: "13rem", flex: 1, minWidth: 0, alignSelf: "flex-start" }}
              days={healthData?.mttr.mttr_days}
              score={healthData?.mttr.score}
              onMoreClick={() => goToMetric(MetricKeys.mttr)}
            />
          </Flex>
        </Styled.ReleasesPageWrap>
      </PageWrap>
    </>
  );
}
